import { PropsWithChildren, Suspense, useEffect } from "react";

import UFOSegment from "@atlaskit/react-ufo/segment";

import { RecommendedTemplatesProvider } from "@/components/confluence/templates/context/recommended-templates-context.tsx";
import { TemplateFuzzySearchContextProvider } from "@/components/confluence/templates/context/template-fuzzy-search-context";
import { TemplateCategory } from "@/components/confluence/templates/template-categories-search";
import {
    TemplateProvider,
    useCategories,
    useCollections,
    usePartnerTemplates
} from "@/content/templates/template.provider";
import type {
    Template,
    TemplateCollection
} from "@/content/templates/template.types";
import ContentNotFoundErrorBoundary from "@/content/utils/content-not-found-error-boundary";
import { CategoryOrder } from "@/templates/software/confluence/templates/confluence-templates-page/src/config/category-template-order.ts";
import { TemplateHeroSection } from "@/templates/software/confluence/templates/confluence-templates-page/src/sections/template-hero-section";
import { TemplateLearnFromLeadersSection } from "@/templates/software/confluence/templates/confluence-templates-page/src/sections/template-learn-from-leaders-section.tsx";
import { UnifiedSearchResults } from "@/templates/software/confluence/templates/confluence-templates-page/src/sections/unified-search-results";

import { SharedHeaderFooterContainer } from "./sections/shared-header-footer-container";
import {
    TemplateCarouselSection,
    SectionDivider
} from "./sections/template-carousel-section";

export type ConfluenceTemplatesUnifiedPageTemplateProps = PropsWithChildren<{
    templates?: Template[];
    collections?: TemplateCollection[];
    categories?: TemplateCategory[];
    partnerTemplates?: Template[];
}>;

const TemplateFuzzySearchContent = () => {
    const partnerTemplates = usePartnerTemplates();
    const collections = useCollections();
    const categories = useCategories();

    return (
        <RecommendedTemplatesProvider>
            <TemplateFuzzySearchContextProvider>
                <TemplateHeroSection />
                <UnifiedSearchResults />
                <SectionDivider />
                <TemplateCarouselSection collections={collections} />
                <TemplateLearnFromLeadersSection
                    templates={partnerTemplates}
                    categories={categories}
                />
            </TemplateFuzzySearchContextProvider>
        </RecommendedTemplatesProvider>
    );
};

export const ConfluenceTemplatesUnifiedPageTemplate = ({
    templates,
    collections,
    categories,
    partnerTemplates
}: ConfluenceTemplatesUnifiedPageTemplateProps) => {
    return (
        <div data-testid="confluence-templates-unified-page">
            <UFOSegment name="confluence-templates-unified-page">
                <SharedHeaderFooterContainer>
                    <ContentNotFoundErrorBoundary>
                        <TemplateProvider
                            templates={templates}
                            collections={collections}
                            categories={categories}
                            partnerTemplates={partnerTemplates}
                            templatesSortBy="name"
                            categoriesSortBy={CategoryOrder}
                        >
                            <Suspense>
                                <TemplateFuzzySearchContent />
                            </Suspense>
                        </TemplateProvider>
                    </ContentNotFoundErrorBoundary>
                </SharedHeaderFooterContainer>
            </UFOSegment>
        </div>
    );
};
